import React from "react";
import { useDrop } from "react-dnd";
import { motion } from "framer-motion";
import { ItemTypes } from "../../../../utils/constants";

export default function MicroscopeOverlay({
  loaded,
  specimenName,
  specimenId,
  image,
  onSpecimenDrop,
}) {
  // drop inside microscope eyepiece
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.SPECIMEN,
      drop: (item) => {
        if (onSpecimenDrop) onSpecimenDrop(item);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [onSpecimenDrop]
  );

  // image from PUBLIC folder
  const src = loaded && image ? `/biology-slides/${image}` : null;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-64 h-60">
        {/* Base */}
        <div className="absolute bottom-0 left-8 right-8 h-6 bg-gray-500 rounded-xl shadow-md" />
        {/* Arm */}
        <div className="absolute left-16 bottom-6 w-24 h-32 bg-gray-300 rounded-3xl rotate-[-18deg] origin-bottom-left shadow-inner" />
        {/* Eyepiece */}
        <div className="absolute left-32 top-4 w-12 h-8 bg-gray-700 rounded-t-xl" />
        {/* Stage */}
        <div className="absolute left-16 bottom-24 w-32 h-4 bg-gray-700 rounded-md" />
        {/* Focus knob */}
        <div className="absolute right-10 bottom-20 w-6 h-6 bg-gray-600 rounded-full border border-gray-800" />

        {/* Eyepiece viewing area — NOW DND ENABLED + ANIMATED */}
        <div
          ref={drop}
          className={[
            "absolute inset-x-20 top-16 w-24 h-24 rounded-full border-4 border-slate-300",
            "bg-slate-900/95 shadow-inner overflow-hidden flex items-center justify-center",
            "transition-all duration-200",
            isOver ? "ring-4 ring-pink-400 scale-105" : "",
          ].join(" ")}
        >
          {!loaded || !src ? (
            <p className="text-[10px] text-slate-200 text-center px-2 leading-tight">
              Drag a slide onto the microscope to view it here
            </p>
          ) : (
            <motion.img
              key={specimenId}
              src={src}
              alt={specimenName}
              className="w-full h-full object-cover"
              initial={{ scale: 0.8, opacity: 0, filter: "blur(8px)" }}
              animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-700 text-center max-w-xs">
        {loaded
          ? `Microscopic view: ${specimenName}`
          : "Microscope ready — drag the correct slide into the eyepiece."}
      </p>
    </div>
  );
}
